import { useMemo } from 'react';

import { getAllJobs } from '../../new-sales-tax/job/list';
import { JobEntity, JobStatus } from '../../new-sales-tax/job/types';
import {
  IUseIndirectTaxExposureJobsParams,
  IUseIndirectTaxExposureJobsResult,
} from './types';

const POLL_INTERVAL_MS = 10_000;

const RUNNING_STATUSES = new Set<JobStatus>([
  JobStatus.PENDING,
  JobStatus.PROCESSING,
  JobStatus.QUEUED,
]);

/**
 * Polls the generic /new-sales-tax/jobs endpoint filtered to entity=EXPOSURE_SYNC.
 * Refresh is paused when no job is in flight. State is derived from the server every
 * poll — no client-side job_id tracking, so UX survives navigation, refresh, and
 * multi-tab use because state always reflects the latest server snapshot.
 *
 * `isLoading` is returned for contract conformance (every `use*` hook must return it
 * per coding standards), but it only flips true on the initial fetch — background
 * polls keep previous data and stay silent.
 */
export function useIndirectTaxExposureJobs({
  corporationId,
  limit = 10,
}: IUseIndirectTaxExposureJobsParams): IUseIndirectTaxExposureJobsResult {
  const { jobs, totalJobs, isLoading, error, mutate } = getAllJobs({
    params: {
      corporation_id: corporationId,
      entity: JobEntity.EXPOSURE_SYNC,
      limit,
    },
    options: {
      // Polling must be fully silent: keep previous data across polls and skip
      // focus-revalidation. The hook still surfaces `isLoading` for contract
      // conformance — but it stays false during background polls.
      keepPreviousData: true,
      revalidateOnFocus: false,
      refreshInterval: (latest) => {
        const latestJobs = latest?.data.jobs ?? [];
        const running = latestJobs.some((job) =>
          RUNNING_STATUSES.has(job.status)
        );
        return running ? POLL_INTERVAL_MS : 0;
      },
    },
  });

  const isRunning = useMemo(
    () => jobs.some((job) => RUNNING_STATUSES.has(job.status)),
    [jobs]
  );

  const latestCompletedAt = useMemo(() => {
    const completed = jobs.find(
      (job) => job.status === JobStatus.COMPLETED && job.completed_at != null
    );
    return completed?.completed_at ?? null;
  }, [jobs]);

  const latestFailureMessage = useMemo(() => {
    const terminal = jobs.find(
      (job) =>
        job.status === JobStatus.FAILED || job.status === JobStatus.COMPLETED
    );
    if (!terminal || terminal.status !== JobStatus.FAILED) {
      return null;
    }
    return terminal.failure_reasons?.[0] ?? 'Exposure sync failed.';
  }, [jobs]);

  return {
    jobs,
    totalJobs,
    isLoading,
    error,
    isRunning,
    latestCompletedAt,
    latestFailureMessage,
    mutate,
  };
}
